import type { ButtonHTMLAttributes, CSSProperties, HTMLAttributes, InputHTMLAttributes, ReactNode, SelectHTMLAttributes, TextareaHTMLAttributes } from 'react'
import { useEffect, useState } from 'react'
import { useEscapeLayer } from '@/hooks/useEscapeLayer'
import { useScrollLock } from '@/hooks/useScrollLock'
import { fallbackOf } from '@/lib/assetFallback'
import { Icon, type IconName } from './icons'

/* ------------------------------- Button ------------------------------- */

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'default' | 'primary' | 'outline' | 'ghost' | 'danger'
  size?: 'sm' | 'md' | 'lg' | 'icon'
  icon?: IconName
  loading?: boolean
  block?: boolean
}

export function Button({
  variant = 'default',
  size = 'md',
  icon,
  loading,
  block,
  children,
  className = '',
  disabled,
  ...rest
}: ButtonProps) {
  const cls = [
    'btn',
    variant !== 'default' ? `btn--${variant}` : '',
    size !== 'md' ? `btn--${size}` : '',
    block ? 'btn--block' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ')
  return (
    <button className={cls} disabled={disabled || loading} {...rest}>
      {loading ? <span className="spinner" /> : icon ? <Icon name={icon} size={size === 'sm' ? 13 : 15} /> : null}
      {children}
    </button>
  )
}

/* -------------------------------- Card -------------------------------- */

/**
 * 卡片外壳。
 *
 * ⚠️ 必须**透传 rest props**（`...rest`）。之前只接 `children/className/padded`，
 * 于是 `<Card data-state={...}>` 这类属性被 React 静默丢掉 ——
 * 写的人以为挂上去了，DOM 里根本查不到，探针恒读 null。
 * 这类「属性写了但没到 DOM」是典型静默失败：typecheck 过、页面正常，
 * 只有真去读 DOM 才发现。`data-*` 是给验收探针的锚点，必须真落下去。
 */
export function Card({
  children,
  className = '',
  padded,
  ...rest
}: { children: ReactNode; className?: string; padded?: boolean } & HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={`card ${padded ? 'card--padded' : ''} ${className}`} {...rest}>
      {children}
    </div>
  )
}

/**
 * 卡片头。传 `onToggle` 即变成可折叠头（整块可点 + 键盘可达 + 末尾 chevron）。
 *
 * ⚠️ 折叠态**不放在 Card 内部实现** —— 收起后卡片只剩这一行，
 * 状态必须由调用方持有（`open` 受控），否则「本页历史」这类要跨页签存活的状态会丢。
 * ⚠️ `onToggle` 生效时，`right` 里**别放按钮**（点击会冒泡到头的 toggle）。
 */
export function CardHead({
  title,
  hint,
  icon,
  lead,
  right,
  open,
  onToggle,
}: {
  title: ReactNode
  hint?: ReactNode
  icon?: IconName
  /**
   * 标题左侧的自定义内容（缩略图 / 状态点…）。
   * 有它就不用为了塞一张缩略图而抛弃 CardHead 自己重写一遍折叠头 ——
   * 「整块可点 + aria-expanded + 键盘可达 + chevron」这套只许有一份实现。
   */
  lead?: ReactNode
  right?: ReactNode
  /** 折叠态；配合 `onToggle` 使用 */
  open?: boolean
  /** 传入即整块可折叠 */
  onToggle?: () => void
}) {
  const foldable = !!onToggle
  return (
    <div
      className={`card__head${foldable ? ' card__head--fold' : ''}`}
      role={foldable ? 'button' : undefined}
      tabIndex={foldable ? 0 : undefined}
      aria-expanded={foldable ? open : undefined}
      onClick={onToggle}
      onKeyDown={
        foldable
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                onToggle?.()
              }
            }
          : undefined
      }
    >
      {lead ?? null}
      {icon ? <Icon name={icon} size={16} style={{ color: 'hsl(var(--primary-strong))' }} /> : null}
      <div className="grow">
        <div className="card__title">{title}</div>
        {hint ? <div className="card__hint">{hint}</div> : null}
      </div>
      {/*
        `right` 槽位必须把点击/按键**拦在这里**。
        ====================================================================
        折叠头的整块都是点击目标（`onClick={onToggle}`），而 `right` 就渲染在
        这个元素内部 —— 任何放进去的按钮，点一下都会**顺带把卡片折叠掉**。

        2026-09-29 真实症状：历史卡头部的「刷新」（当时叫「重新读取」），点一次卡片收起、
        界面只剩「0 条 · 点击展开」，读起来像「点了没反应反而把东西弄没了」。
        而这类 bug **静态看不出来** —— 按钮的 onClick 明明是对的。

        `display: contents`（见 global.css 的 `.card__acts`）让这层包装
        **不产生盒子**，布局与加包装之前逐像素一致：它只拦事件，不改排版。
        键盘也一起拦：焦点在按钮上按 Enter/Space 时，keydown 会冒到折叠头，
        不拦就是「按一下回车，按钮生效 + 卡片收起」两个动作。
      */}
      {right ? (
        <span
          className="card__acts"
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        >
          {right}
        </span>
      ) : null}
      {foldable ? <Icon name="chevron" size={15} className="card__fold" data-open={!!open} /> : null}
    </div>
  )
}

export function CardBody({ children, tight }: { children: ReactNode; tight?: boolean }) {
  return <div className={`card__body${tight ? ' card__body--tight' : ''}`}>{children}</div>
}

/* -------------------------------- Field -------------------------------- */

export function Field({
  label,
  hint,
  required,
  children,
  right,
}: {
  label?: ReactNode
  hint?: ReactNode
  required?: boolean
  children: ReactNode
  right?: ReactNode
}) {
  return (
    <div className="field">
      {label ? (
        <div className="field__label">
          <span>
            {label}
            {required ? <span className="field__req"> *</span> : null}
          </span>
          {right ? <span style={{ marginLeft: 'auto' }}>{right}</span> : null}
        </div>
      ) : null}
      {children}
      {hint ? <div className="field__hint">{hint}</div> : null}
    </div>
  )
}

export const Input = (p: InputHTMLAttributes<HTMLInputElement>) => <input className="input" {...p} />
export const Textarea = (p: TextareaHTMLAttributes<HTMLTextAreaElement>) => <textarea className="textarea" {...p} />
export const Select = (p: SelectHTMLAttributes<HTMLSelectElement>) => <select className="select" {...p} />

/* ------------------------------ Segmented ------------------------------ */

export function Segmented<T extends string>({
  value,
  onChange,
  options,
}: {
  value: T
  onChange: (v: T) => void
  options: { value: T; label: ReactNode }[]
}) {
  return (
    <div className="segmented">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          className="segmented__item"
          data-active={o.value === value}
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  )
}

/* ----------------------------- GroupedSelect ---------------------------- */

/**
 * 分组下拉 —— 原生 `<select>` + `<optgroup>`，**候选多且分语义时才用**。
 *
 * 为什么不用 `Segmented`：候选从 4 项涨到十几个（当前模型能出的比例，
 * duomi 直连 13 个），一行按钮会换行占掉半屏；而且「官方尺寸 / 平台常用 /
 * 其他比例」这三种**语义不同**的东西平铺在一起，用户分不出哪一项是官方要求。
 * 原生 select 顺带解决另一个问题：窄容器里中文标签不会被逐字挤成竖排。
 *
 * 空值那一项是「官方尺寸」（`''` = 逐张按模块规格），这是**默认值**，
 * 它必须排在第一组第一项 —— 用户不选就是它。
 */
export function GroupedSelect({
  value,
  onChange,
  groups,
  disabled,
  ariaLabel,
}: {
  value: string
  onChange: (v: string) => void
  groups: { id: string; title: string; options: { value: string; label: string }[] }[]
  disabled?: boolean
  ariaLabel?: string
}) {
  return (
    <Select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      aria-label={ariaLabel}
    >
      {groups.map((g) => (
        <optgroup key={g.id} label={g.title}>
          {g.options.map((o) => (
            // 空串做 key 会让 React 报警且可能错位，所以显式加前缀
            <option key={`v:${o.value}`} value={o.value}>
              {o.label}
            </option>
          ))}
        </optgroup>
      ))}
    </Select>
  )
}

/* ------------------------------ CountInput ------------------------------ */

/**
 * 张数输入框 —— **自己敲数字**。
 *
 * 用户 2026-09-30 原话：「我是说可以我们自己输入数量！！」。
 * 上一版是 `Segmented` 按钮组，上限还锁在官方图位数上（TikTok Shop 8 个图位
 * 只能点 1–8），想多出几张**根本没有入口**。
 *
 * 为什么带 −/+ 又留输入框：改一两张时按钮快，凑一批时直接敲快。
 * 两处必须长得一模一样（详情页「输出张数」/ 批量页「每个产品出几张」），
 * 所以它长在这里而不是各写一遍 —— 抄第二遍必然分叉（步进 / 夹取 / 单位 / 空值行为）。
 *
 * ⚠️ **受控 number input 的经典坑**：直接把 `value` 绑上去的话，用户清空输入框
 * 时会被立刻夹回 `min` —— 结果是删不掉、只能全选重输。所以加一层本地草稿
 * （`draft`）：输入期间显示用户敲的原样，失焦或外部改值时归位。
 */
export function CountInput({
  value,
  onChange,
  min = 1,
  max = 99,
  unit = '张',
  disabled,
}: {
  value: number
  onChange: (n: number) => void
  min?: number
  max?: number
  unit?: string
  disabled?: boolean
}) {
  const [draft, setDraft] = useState<string | null>(null)

  // 外部改了值（换平台 / 换档位把张数收敛回来）→ 丢掉草稿，显示真值
  useEffect(() => setDraft(null), [value])

  const clamp = (raw: unknown) => {
    const n = Math.floor(Number(raw))
    if (!Number.isFinite(n)) return min
    return Math.min(max, Math.max(min, n))
  }

  return (
    <div className="count-input">
      <Button
        size="sm"
        variant="outline"
        disabled={disabled || value <= min}
        onClick={() => onChange(clamp(value - 1))}
        aria-label="减少一张"
      >
        −
      </Button>
      <input
        className="input count-input__box"
        type="number"
        inputMode="numeric"
        min={min}
        max={max}
        disabled={disabled}
        aria-label="张数"
        value={draft ?? String(value)}
        onChange={(e) => {
          const raw = e.target.value
          setDraft(raw)
          if (raw !== '' && Number.isFinite(Number(raw))) onChange(clamp(raw))
        }}
        onBlur={() => setDraft(null)}
      />
      <span className="count-input__unit">{unit}</span>
      <Button
        size="sm"
        variant="outline"
        disabled={disabled || value >= max}
        onClick={() => onChange(clamp(value + 1))}
        aria-label="增加一张"
      >
        +
      </Button>
    </div>
  )
}

/* -------------------------------- Chips -------------------------------- */

/**
 * 单选 chip 行 —— **全站唯一一份**。
 *
 * 合并页（`/detail-page` 的出图模式、`/video-studio` 的视频档位）的「换整条工作流」
 * 选择器都用它，页面上不再手抄 `<button className="chip" data-active=…>` ——
 * 手抄的那几份已经各自长出了不同的 tooltip / 布局写法，正是本项目铁律
 * 「同一控件只许一份实现」要消灭的东西。
 *
 * 三个可选参数都只为合并页那种「页签外的根决策」加：
 *   `titles`    —— 每个选项一句话说明（悬停可见）。换模式 = 换整条工作流，
 *                  光看名字（「模仿版式」「多语言版」）说不清产出什么。
 *   `className` —— 布局钩子。默认 `.chips`；页签外的模式行传 `chip-row`
 *                  （与「平台条 / 规模条」同一层的选择区，间距口径不同）。
 *   `style`     —— 只为首行与下方卡片之间的间距，各页历史口径不同，不统一进 CSS。
 */
export function Chips<T extends string>({
  value,
  onChange,
  options,
  titles,
  className = 'chips',
  style,
}: {
  value: T | null
  onChange: (v: T) => void
  options: { value: T; label: string }[]
  titles?: Partial<Record<T, string>>
  className?: string
  style?: CSSProperties
}) {
  return (
    <div className={className} style={style}>
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          className="chip"
          data-active={o.value === value}
          title={titles?.[o.value]}
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  )
}

/* -------------------------------- Badge -------------------------------- */

export function Badge({
  children,
  tone = 'neutral',
  icon,
}: {
  children: ReactNode
  tone?: 'neutral' | 'primary' | 'success' | 'warning' | 'danger' | 'info'
  icon?: IconName
}) {
  return (
    <span className={`badge badge--${tone}`}>
      {icon ? <Icon name={icon} size={11} /> : null}
      {children}
    </span>
  )
}

export const STATE_TONE: Record<string, { tone: 'neutral' | 'primary' | 'success' | 'warning' | 'danger' | 'info'; label: string }> = {
  queued: { tone: 'info', label: '排队中' },
  running: { tone: 'primary', label: '生成中' },
  succeeded: { tone: 'success', label: '已完成' },
  partial: { tone: 'warning', label: '部分完成' },
  failed: { tone: 'danger', label: '失败' },
}

/* ------------------------------- Progress ------------------------------- */

export function Progress({ value, indeterminate }: { value: number; indeterminate?: boolean }) {
  return (
    <div className="progress">
      <div
        className={`progress__bar${indeterminate ? ' progress__bar--indeterminate' : ''}`}
        style={{ width: `${Math.min(100, Math.max(0, value))}%` }}
      />
    </div>
  )
}

/* -------------------------------- Empty -------------------------------- */

export function Empty({
  icon = 'box',
  title,
  text,
  action,
}: {
  icon?: IconName
  title: string
  text?: ReactNode
  action?: ReactNode
}) {
  return (
    <div className="empty">
      <div className="empty__icon">
        <Icon name={icon} size={22} />
      </div>
      <div className="empty__title">{title}</div>
      {text ? <div className="empty__text">{text}</div> : null}
      {action ? <div style={{ marginTop: 16 }}>{action}</div> : null}
    </div>
  )
}

/* ------------------------------ Modal / Drawer ------------------------------ */

export function Modal({
  open,
  onClose,
  title,
  icon,
  children,
  footer,
  width,
}: {
  open: boolean
  onClose: () => void
  title: ReactNode
  icon?: IconName
  children: ReactNode
  footer?: ReactNode
  width?: number
}) {
  // Esc 关自己，但**不替上面那层关**（浮层栈见 hooks/useEscapeLayer）。
  // 曾经是这里各写一个 window 监听：抽屉里弹出价格确认时，一次 Esc 会
  // 把抽屉和弹窗一起关掉 —— 用户想取消这次生成，填了一半的表单也跟着没了。
  useEscapeLayer(onClose, open)
  // 居中弹窗同样会带着背景滚 —— 与 Drawer / 放大遮罩统一走同一个 hook
  useScrollLock(open)

  if (!open) return null
  return (
    <>
      <div className="scrim" onClick={onClose} />
      <div className="modal" style={width ? { width: `min(${width}px, calc(100vw - 32px))` } : undefined} role="dialog" aria-modal="true">
        <div className="modal__head">
          {icon ? <Icon name={icon} size={18} style={{ color: 'hsl(var(--primary-strong))' }} /> : null}
          <div className="modal__title grow">{title}</div>
          <Button variant="ghost" size="icon" icon="x" onClick={onClose} aria-label="关闭" />
        </div>
        <div className="modal__body">{children}</div>
        {footer ? <div className="modal__foot">{footer}</div> : null}
      </div>
    </>
  )
}

export function Drawer({
  open,
  onClose,
  title,
  icon,
  children,
  footer,
  width,
  className,
}: {
  open: boolean
  onClose: () => void
  title: ReactNode
  icon?: IconName
  children: ReactNode
  footer?: ReactNode
  width?: number
  /** 追加类名，供调用方按内容调密度（如任务抽屉把图集放宽） */
  className?: string
}) {
  /*
   * Esc 关闭 + 锁背景滚动。
   *
   * `Modal` 早就有 Esc，`Drawer` 一直没有 —— 同一个项目里两个弹层行为不一致，
   * 用户按 Esc 时抽屉纹丝不动，会以为快捷键坏了。锁滚动同理：不锁的话滚抽屉
   * 会把底下那层列表一起带着滚。
   * 放在这里而不是各调用点：调用点越来越多（素材库、任务详情），
   * 抄漏一处就是一个「这个抽屉 Esc 关不掉」的 bug。
   *
   * ⚠️ 2026-10-09：Esc 交给 `useEscapeLayer`（浮层栈），不再自己挂监听。
   * 抽屉里现在会**再叠一层**（「生成新模特」抽屉 → 价格确认弹窗），
   * 各挂一个监听的结果是一次 Esc 关两层。理由与复现见那个 hook 的头注。
   */
  useEscapeLayer(onClose, open)
  // 锁背景滚动：不锁的话滚抽屉会把底下那层列表一起带着滚
  useScrollLock(open)

  if (!open) return null
  return (
    <>
      <div className="scrim" onClick={onClose} />
      <aside
        className={`drawer${className ? ` ${className}` : ''}`}
        style={width ? { width: `min(${width}px, 96vw)` } : undefined}
        role="dialog"
        aria-modal="true"
      >
        <div className="modal__head">
          {icon ? <Icon name={icon} size={18} style={{ color: 'hsl(var(--primary-strong))' }} /> : null}
          <div className="modal__title grow">{title}</div>
          <Button variant="ghost" size="icon" icon="x" onClick={onClose} aria-label="关闭" />
        </div>
        <div className="modal__body grow">{children}</div>
        {footer ? <div className="modal__foot">{footer}</div> : null}
      </aside>
    </>
  )
}

/* ------------------------------ CopyButton ------------------------------ */

export function CopyButton({ text, label = '复制' }: { text: string; label?: string }) {
  const [done, setDone] = useState(false)
  return (
    <Button
      variant="outline"
      size="sm"
      icon={done ? 'check' : 'copy'}
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(text)
        } catch {
          const ta = document.createElement('textarea')
          ta.value = text
          document.body.appendChild(ta)
          ta.select()
          document.execCommand('copy')
          ta.remove()
        }
        setDone(true)
        window.setTimeout(() => setDone(false), 1600)
      }}
    >
      {done ? '已复制' : label}
    </Button>
  )
}

/* ------------------------------ ClearButton ------------------------------ */

/**
 * 清空按钮（内联二次确认，唯一实现）。
 *
 * 为什么不用 Modal 二次确认：它清的东西**都不销毁任何产物**——
 * 结果卡那颗只收起右栏的产出预览（记录与产出都在，见 `clearResults`），
 * 素材卡那颗只清这一行的参考图（原图还在用户电脑上）。
 * 为一次可逆操作弹窗打断流程不值当，所以做成「点一下变确认，4 秒不点自动复位」。
 *
 * ⚠️ 别在别处再写一份「点了变确认」的按钮 —— 计时/复位/危险态三件事很容易抄漏一件。
 */
export function ClearButton({
  count,
  unit = '个',
  label = '清空',
  title,
  iconOnly = false,
  onClear,
}: {
  count: number
  unit?: string
  label?: string
  title?: string
  /**
   * 图标形态 —— 给**放不下两个字**的地方用（表格单元格里的参考图格）。
   *
   * 仍然走同一个组件、同一套二次确认：点了先变危险态、4 秒后自己放下、
   * 再点一次才真清。收起来的只是文案，**不是那个确认步骤** ——
   * 把确认也一起省掉，就等于给「手滑清空整行参考图」开了一条没有护栏的路。
   * 所以危险态里那行「确认清空 N 张」照常显示（那时它有空间了）。
   */
  iconOnly?: boolean
  onClear: () => void
}) {
  const [armed, setArmed] = useState(false)

  // 拿起后放着不管要能自己放下，否则按钮长期停在「确认清空」的危险态上，
  // 下次回来随手一点就真清了。
  useEffect(() => {
    if (!armed) return
    const t = window.setTimeout(() => setArmed(false), 4000)
    return () => window.clearTimeout(t)
  }, [armed])

  return (
    <Button
      size="sm"
      variant={armed ? 'danger' : 'ghost'}
      icon={armed ? undefined : 'trash'}
      title={armed ? '再点一次即清空' : title}
      aria-label={armed ? `确认清空 ${count} ${unit}` : label}
      onClick={() => {
        if (!armed) {
          setArmed(true)
          return
        }
        setArmed(false)
        onClear()
      }}
    >
      {armed ? `确认清空 ${count} ${unit}` : iconOnly ? null : label}
    </Button>
  )
}

/* -------------------------------- Stat -------------------------------- */

export function Stat({
  label,
  value,
  delta,
  deltaTone = 'flat',
  icon,
  suffix,
}: {
  label: string
  value: ReactNode
  delta?: string
  deltaTone?: 'up' | 'down' | 'flat'
  icon?: IconName
  suffix?: string
}) {
  return (
    <div className="stat">
      <div className="stat__label">
        {icon ? <Icon name={icon} size={13} /> : null}
        {label}
      </div>
      <div className="stat__value">
        {value}
        {suffix ? <span style={{ fontSize: '0.5em', fontWeight: 600, marginLeft: 3 }}>{suffix}</span> : null}
      </div>
      {delta ? <div className={`stat__delta${deltaTone === 'flat' ? '' : ` stat__delta--${deltaTone}`}`}>{delta}</div> : null}
    </div>
  )
}

/* ------------------------------- Section ------------------------------- */

export function SectionTitle({
  title,
  desc,
  right,
}: {
  title: ReactNode
  desc?: ReactNode
  right?: ReactNode
}) {
  return (
    <div className="row row--between" style={{ marginBottom: 12 }}>
      <div>
        <div style={{ fontWeight: 660, fontSize: 'var(--text-lg)' }}>{title}</div>
        {desc ? <div className="text-xs muted" style={{ marginTop: 2 }}>{desc}</div> : null}
      </div>
      {right}
    </div>
  )
}

/* ------------------------------ Dropzone ------------------------------ */

export function Dropzone({
  label,
  hint,
  multiple,
  onFiles,
  compact,
  accept = 'image/*,video/*,audio/*',
}: {
  label: string
  hint?: string
  multiple?: boolean
  onFiles: (files: File[]) => void
  compact?: boolean
  /**
   * 文件类型。默认是「素材」那一套（图片 / 视频 / 音频）—— 参考图上传、批量补图
   * 都用它。**商品资产的 CSV 导入**要的是 `.csv,.txt`，所以这里开一个口子，
   * 而不是在那边再写一个长得一样的拖放框（拖放状态、busy、清 value 这三件事
   * 抄一遍就会分叉）。
   */
  accept?: string
}) {
  const [over, setOver] = useState(false)
  const [busy, setBusy] = useState(false)

  const handle = async (files: FileList | null) => {
    if (!files || !files.length) return
    setBusy(true)
    try {
      onFiles(Array.from(files))
    } finally {
      setBusy(false)
    }
  }

  return (
    <label
      className={`dropzone${over ? ' dropzone--over' : ''}`}
      style={compact ? { padding: 12 } : undefined}
      onDragOver={(e) => {
        e.preventDefault()
        setOver(true)
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        e.preventDefault()
        setOver(false)
        void handle(e.dataTransfer.files)
      }}
    >
      <input
        type="file"
        accept={accept}
        multiple={multiple}
        style={{ display: 'none' }}
        onChange={(e) => {
          void handle(e.target.files)
          e.target.value = ''
        }}
      />
      <div className="dropzone__icon">{busy ? <span className="spinner" /> : <Icon name="upload" size={20} />}</div>
      <div className="dropzone__label">{label}</div>
      {hint ? <div className="dropzone__hint">{hint}</div> : null}
    </label>
  )
}

/* -------------------------------- Thumbs -------------------------------- */

export function Thumbs({
  urls,
  onRemove,
}: {
  urls: string[]
  onRemove?: (url: string) => void
}) {
  // 图床图可能被删/被墙，加载失败时退到本地副本，再失败才显示占位
  const [broken, setBroken] = useState<Record<string, string | false>>({})

  if (!urls.length) return null
  return (
    <div className="thumbs">
      {urls.map((u, i) => {
        const state = broken[u]
        const src = typeof state === 'string' ? state : state === false ? '' : u
        return (
          <div className="thumb" key={`${u}-${i}`}>
            {src ? (
              <img
                src={src}
                alt={`参考图 ${i + 1}`}
                onError={() => {
                  if (state === undefined) {
                    const local = fallbackOf(u)
                    setBroken((m) => ({ ...m, [u]: local ?? false }))
                  } else {
                    setBroken((m) => ({ ...m, [u]: false }))
                  }
                }}
              />
            ) : (
              <div className="thumb__fallback" title="图片加载失败">
                <Icon name="alert" size={13} />
              </div>
            )}
            {onRemove ? (
              <button className="thumb__x" type="button" onClick={() => onRemove(u)} aria-label="移除">
                <Icon name="x" size={11} />
              </button>
            ) : null}
          </div>
        )
      })}
    </div>
  )
}
